import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { VALIDATION } from '../../utils/constants';

const Login = () => {
  const [formData, setFormData] = useState({ usuario: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error cuando el usuario empiece a escribir
    if (error) setError('');
  };

  const validateForm = () => {
    const { usuario, password } = formData;

    if (!usuario.trim()) {
      setError('El usuario es requerido');
      return false;
    }

    if (usuario.trim().length < VALIDATION.USER.MIN_USERNAME_LENGTH) {
      setError(`El usuario debe tener al menos ${VALIDATION.USER.MIN_USERNAME_LENGTH} caracteres`);
      return false;
    }

    if (!password) {
      setError('La contraseña es requerida');
      return false;
    }

    if (password.length < VALIDATION.USER.MIN_PASSWORD_LENGTH) {
      setError(`La contraseña debe tener al menos ${VALIDATION.USER.MIN_PASSWORD_LENGTH} caracteres`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData.usuario.trim(), formData.password);
      
      if (!result.success) {
        setError(result.message);
      }
      // Si es exitoso, el AuthContext redirige automáticamente
    } catch (error) {
      console.error('Login error:', error);
      setError('Error inesperado. Por favor, intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-2">
          <img
            src="/logo192.png"
            alt="Yevox"
            className="w-60 h-60 object-contain mx-auto" style={{marginBottom:'-60px'}}
            onError={e => {
              e.target.style.display = 'none'
              document.getElementById('logo-fallback-rest').style.display = 'flex'
            }}
          />
          <div id="logo-fallback-rest" className="bg-blue-600 w-20 h-20 rounded-full items-center justify-center mx-auto mb-4 shadow-lg" style={{display:'none'}}>
            <span className="text-white text-3xl font-bold">🏪</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Yevox Restaurante</h1>
          <p className="text-gray-600">Panel de administración de restaurante</p>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-6">
            {/* Campo Usuario */}
            <div>
              <label htmlFor="usuario" className="block text-sm font-medium text-gray-700 mb-2">
                Usuario
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-xl">👤</span>
                </div>
                <input
                  id="usuario"
                  name="usuario"
                  type="text"
                  value={formData.usuario}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="block w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                  placeholder="Ingresa tu usuario"
                  disabled={isLoading}
                  autoComplete="username"
                  maxLength={VALIDATION.USER.MAX_USERNAME_LENGTH}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Mínimo {VALIDATION.USER.MIN_USERNAME_LENGTH} caracteres
              </p>
            </div>

            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-xl">🔒</span>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="block w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                  placeholder="Ingresa tu contraseña"
                  disabled={isLoading}
                  autoComplete="current-password"
                  maxLength={VALIDATION.USER.MAX_PASSWORD_LENGTH}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Mínimo {VALIDATION.USER.MIN_PASSWORD_LENGTH} caracteres
              </p>
            </div>

            {/* Mensaje de Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <span className="text-red-400 text-xl mr-3 mt-0.5">⚠️</span>
                  <div>
                    <p className="text-red-700 text-sm font-medium">Error de autenticación</p>
                    <p className="text-red-600 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Botón de Submit */}
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Iniciando sesión...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span className="mr-2">🚀</span>
                  Iniciar Sesión
                </div>
              )}
            </button>
          </div>

          {/* Información de prueba
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <span className="text-blue-500 text-lg mr-2">💡</span>
              <div>
                <p className="text-sm font-medium text-blue-900">Cuenta de prueba</p>
                <p className="text-xs text-blue-700 mt-1">
                  Usuario: <code className="bg-blue-100 px-1 rounded">henry</code> | 
                  Contraseña: <code className="bg-blue-100 px-1 rounded">123</code>
                </p>
              </div>
            </div>
          </div> */}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            © 2025 Yevox. Sistema de gestión administrativa.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;